import { createContext, useContext } from 'react';

/**
 * What a caller outside the region can ask it to do - commands that are not hooks, and so cannot
 * read the context. Handed over by putting a `ref` on the region.
 */
export type ScrollRegionHandle = {
  scrollToBottom: (options?: { instant?: boolean }) => void;
  scrollToTop: (options?: { instant?: boolean }) => void;
};

export type ScrollRegionContextValue = {
  /** Whether the reader is far enough from the end to be worth offering a way back. */
  isAwayFromBottom: boolean;
  /**
   * Registers the box docked at the region's bottom edge, so the region can publish its height
   * and hold that much clear when something is scrolled into view near the end. Put the returned
   * ref on the docked element.
   */
  footerRef: React.RefCallback<HTMLElement>;
  /**
   * Runs the callback whenever the region has scrolled or changed size, and **not** while the
   * layout is being moved by something else. Returns the unsubscribe.
   *
   * **One loop for the region, not one per watcher.** Each subscriber reading the box for itself
   * meant a forced layout per watcher per frame, on the same element, during exactly the
   * animations the pause exists to sit out.
   */
  onMeasure: (callback: () => void) => () => void;
  /**
   * The box itself, for a caller measuring against it - a position watcher, a listener of its
   * own. `null` until it has mounted.
   */
  regionRef: React.MutableRefObject<HTMLDivElement | null>;
  scrollToBottom: (options?: { instant?: boolean }) => void;
  scrollToTop: (options?: { instant?: boolean }) => void;
};

export const ScrollRegionContext =
  createContext<ScrollRegionContextValue | null>(null);

/**
 * The `ScrollRegion` this is inside. Throws outside one, because every caller is asking about a
 * box that has to exist - a silent no-op would read as the region simply never moving.
 */
export const useScrollRegion = () => {
  const value = useContext(ScrollRegionContext);

  if (!value) {
    throw new Error('useScrollRegion must be used inside a ScrollRegion');
  }

  return value;
};
